import { useCallback, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { AccountStatus, useIdentify } from '@hungryroot/customer';
import {
  CYOBottomCallout,
  ViewCartButton,
} from '@hungryroot/exp-171-mhr-redesign';
import {
  useDefaultGroceriesRoute,
  useDefaultRecipesRoute,
} from '@hungryroot/exp-174-personalized-suggestions-tab';
import { useExperiment } from '@hungryroot/experiments';
import { CaretLeft, Person, Person2 } from '@hungryroot/icons';
import { useNextOrderV2 } from '@hungryroot/orders';
import { Button, Heading, Paragraph } from '@hungryroot/ramen';
import {
  EDIT_DELIVERY_GROCERIES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  isEqualPathnames,
  isEqualToAtLeastOnePathname,
  MY_HUNGRYROOT_ROUTE,
  NEXT_ORDER_ROUTE,
  ORDER_ROUTE,
  SUPPORT_ARTICLE_ROUTE,
} from '@hungryroot/routes';
import { buttonClickedEvent } from '@hungryroot/segment';
import {
  Search,
  type SearchProps,
} from '../../../pages/customize/components/Search/Search';
import { CYOModal } from '../../CYO/CYOModal/CYOModal';
import { AccountDropdownContent } from '../AccountDropdownContent/AccountDropdownContent';
import { DropdownPopover } from '../DropdownPopover/DropdownPopover';
import styles from './DesktopNav.module.css';

export const LOGO_PATH = `/public/img/logos/hr-logo-smile-red.svg`;

type DesktopNavProps = {
  cartSummary: ReactNode;
  testid?: string;
  credit: string;
  ordersContent: ReactNode;
  accountStatus: AccountStatus;
  search: SearchProps;
  onOnboardingComplete?: () => void;
  onViewCartButtonClick: ({ orderId }: { orderId: number }) => void;
};

const shopRoutes = Object.freeze([
  EDIT_DELIVERY_RECIPES_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_GROCERIES_FOR_YOU_ROUTE,
]);

export const DesktopNav = (props: DesktopNavProps) => {
  const {
    testid,
    cartSummary,
    credit,
    ordersContent,
    accountStatus,
    search,
    onOnboardingComplete,
    onViewCartButtonClick,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const { order } = useNextOrderV2();
  const redesignedMhrVariant = useExperiment(171);

  const isCancelled = accountStatus === AccountStatus.Cancelled;
  const isPaused = accountStatus === AccountStatus.Paused;

  const defaultRecipesRoute = useDefaultRecipesRoute();
  const defaultGroceriesRoute = useDefaultGroceriesRoute();

  if (redesignedMhrVariant === undefined) {
    return null;
  }

  if (redesignedMhrVariant > 0) {
    if (isEqualToAtLeastOnePathname(location.pathname, shopRoutes)) {
      return (
        <ShopDesktopNavExp171
          onViewCartButtonClick={onViewCartButtonClick}
          search={search}
          testid={testid}
          isCancelled={isCancelled}
          isPaused={isPaused}
        />
      );
    } else {
      return (
        <DesktopNavExp171
          onOnboardingComplete={onOnboardingComplete}
          testid={testid}
          isCancelled={isCancelled}
          isPaused={isPaused}
          credit={credit}
          accountStatus={accountStatus}
          ordersContent={ordersContent}
        />
      );
    }
  }

  return (
    <nav className={styles['desktop-nav']} data-testid={testid}>
      <div className={styles['desktop-nav-left']}>
        <Logo onOnboardingComplete={onOnboardingComplete} testid={testid} />
        <Button
          style="inverted"
          prominence="primary"
          size="medium"
          textPrimary="Recipes"
          className={styles['link']}
          testid={`${testid}-link-recipes`}
          onClick={() => {
            onOnboardingComplete && onOnboardingComplete();

            buttonClickedEvent({
              target_url_path: defaultRecipesRoute,
              order_id: order?.id ?? null,
              button_name: 'Recipes',
              page_position: 'Top Nav',
            });
            history.push(defaultRecipesRoute);
          }}
        ></Button>
        <Button
          style="inverted"
          prominence="primary"
          size="medium"
          textPrimary="Groceries"
          className={styles['link']}
          testid={`${testid}-link-groceries`}
          onClick={() => {
            onOnboardingComplete && onOnboardingComplete();

            buttonClickedEvent({
              target_url_path: defaultGroceriesRoute,
              order_id: order?.id ?? null,
              button_name: 'Groceries',
              page_position: 'Top Nav',
            });
            history.push(defaultGroceriesRoute);
          }}
        ></Button>
      </div>
      <Search {...search} />
      <div className={styles['desktop-nav-right']}>
        <OrdersDropdown
          isCancelled={isCancelled}
          isPaused={isPaused}
          ordersContent={ordersContent}
          testid={testid}
          onOnboardingComplete={onOnboardingComplete}
          position={isCancelled || isPaused ? 'right' : 'left'}
        />
        <AccountDropdown
          isCancelled={isCancelled}
          isPaused={isPaused}
          testid={testid}
          onOnboardingComplete={onOnboardingComplete}
          credit={credit}
          accountStatus={accountStatus}
          position={isCancelled || isPaused ? 'right' : 'left'}
        />
        {cartSummary ? <div className={styles.line}></div> : null}
        {cartSummary}
      </div>
    </nav>
  );
};

type DesktopNavExp171Props = {
  onOnboardingComplete?: () => void;
  testid?: string;
  isCancelled: boolean;
  isPaused: boolean;
  credit: string;
  accountStatus: AccountStatus;
  ordersContent: ReactNode;
  renderLeftNav?: () => ReactNode;
};

function DesktopNavExp171(props: DesktopNavExp171Props) {
  const {
    onOnboardingComplete,
    testid,
    isCancelled,
    isPaused,
    credit,
    accountStatus,
    ordersContent,
  } = props;

  const location = useLocation();
  const history = useHistory();

  const showBackButton =
    isEqualPathnames(location.pathname, NEXT_ORDER_ROUTE) ||
    location.pathname.includes(ORDER_ROUTE) ||
    location.pathname.includes(SUPPORT_ARTICLE_ROUTE);

  return (
    <nav
      className={clsx(styles['desktop-nav'], styles['desktop-nav-exp171'])}
      data-testid={testid}
    >
      {showBackButton ? (
        <div className={styles['desktop-nav-left-exp171']}>
          <button
            onClick={() => {
              history.push(MY_HUNGRYROOT_ROUTE);
            }}
            className={styles['back-button']}
          >
            <CaretLeft className={styles['back-button-icon']} />
            <Heading variant="H6">Back</Heading>
          </button>
        </div>
      ) : null}

      <Logo onOnboardingComplete={onOnboardingComplete} testid={testid} />
      <div className={styles['desktop-nav-right-exp171']}>
        <OrdersDropdown
          isCancelled={isCancelled}
          isPaused={isPaused}
          ordersContent={ordersContent}
          testid={testid}
          onOnboardingComplete={onOnboardingComplete}
          position="right"
        />
        <AccountDropdown
          isCancelled={isCancelled}
          isPaused={isPaused}
          testid={testid}
          onOnboardingComplete={onOnboardingComplete}
          credit={credit}
          accountStatus={accountStatus}
          position="right"
        />
      </div>
    </nav>
  );
}

type ShopDesktopNavExp171Props = {
  search: SearchProps;
  testid?: string;
  onViewCartButtonClick: ({ orderId }: { orderId: number }) => void;
  isCancelled: boolean;
  isPaused: boolean;
};

function ShopDesktopNavExp171(props: ShopDesktopNavExp171Props) {
  const { search, testid, onViewCartButtonClick, isCancelled, isPaused } =
    props;

  const history = useHistory();
  const [pointDiffFromThreshold, setPointDiffFromThreshold] = useState(0);

  // Memoized callback to prevent the animation from `<ViewCartButton />` from triggering
  // this function every re-render due to the function reference changing.
  const onFinishedAnimation = useCallback(
    (args: {
      buttonStyle: 'default' | 'muted' | 'warning';
      points: number;
      threshold: number;
    }) => {
      if (args.buttonStyle === 'warning' && args.threshold - args.points > 0) {
        setPointDiffFromThreshold(args.threshold - args.points);
      } else {
        setPointDiffFromThreshold(0);
      }
    },
    []
  );

  return (
    <nav
      className={clsx(
        styles['desktop-nav-exp171'],
        styles['desktop-nav-shop-exp171']
      )}
      data-testid={testid}
    >
      <div className={styles['desktop-nav-left-exp171']}>
        <button
          onClick={() => {
            history.push(NEXT_ORDER_ROUTE, { from: 'edit-delivery' });
          }}
          className={styles['back-button']}
        >
          <CaretLeft className={styles['back-button-icon']} />
          <Heading variant="H6">
            {isCancelled || isPaused ? 'Back' : 'Back to order'}
          </Heading>
        </button>
      </div>
      <Search {...search} className={styles['search-container-exp171']} />
      <div className={styles['desktop-nav-right-exp171']}>
        {pointDiffFromThreshold > 0 ? (
          <div className={styles['credit-minimum-message']}>
            <Paragraph variant="caption">
              Add{' '}
              <span className={styles['credit-minimum-message-number']}>
                {pointDiffFromThreshold}{' '}
                {pointDiffFromThreshold === 1 ? 'credit' : 'credits'}
              </span>
              <br />
              to meet the minimum
            </Paragraph>
          </div>
        ) : null}
        <ViewCartButton
          onClick={onViewCartButtonClick}
          onFinishedAnimation={onFinishedAnimation}
        />
      </div>
      <CYOBottomCallout
        className={styles['cyo-bottom-callout']}
        renderCYOModal={(props) => {
          if (props.customerCYO) {
            return <CYOModal {...props} customerCYO={props.customerCYO} />;
          }

          return null;
        }}
      />
    </nav>
  );
}

type LogoProps = {
  onOnboardingComplete?: () => void;
  testid?: string;
};

function Logo(props: LogoProps) {
  const { onOnboardingComplete, testid } = props;

  const { order } = useNextOrderV2();

  return (
    <Link
      to={MY_HUNGRYROOT_ROUTE}
      data-testid={`${testid}-logo-link`}
      onClick={() => {
        onOnboardingComplete && onOnboardingComplete();

        buttonClickedEvent({
          target_url_path: MY_HUNGRYROOT_ROUTE,
          order_id: order?.id ?? null,
          button_name: 'Home',
          page_position: 'Top Nav',
        });
      }}
    >
      <img
        data-testid={`${testid}-logo`}
        className={styles.logo}
        alt="Hungryroot logo"
        height="67"
        width="143"
        src={LOGO_PATH}
      />
    </Link>
  );
}

type OrdersDropdownProps = {
  onOnboardingComplete?: () => void;
  isCancelled: boolean;
  isPaused: boolean;
  ordersContent: ReactNode;
  testid?: string;
  position?: 'left' | 'right';
};

function OrdersDropdown(props: OrdersDropdownProps) {
  const {
    onOnboardingComplete,
    isCancelled,
    isPaused,
    ordersContent,
    testid,
    position,
  } = props;

  const location = useLocation();
  const { order } = useNextOrderV2();

  return (
    <DropdownPopover
      className={clsx(
        styles['orders-dropdown'],
        isCancelled || isPaused
          ? styles['orders-dropdown-width-right']
          : styles['orders-dropdown-width']
      )}
      buttonContent="Orders"
      buttonClassNames={styles['link']}
      buttonHref="orders"
      testid={testid}
      buttonTestId="orders"
      onClick={() => {
        onOnboardingComplete && onOnboardingComplete();

        buttonClickedEvent({
          target_url_path: `${location.pathname}?orders`,
          order_id: order?.id ?? null,
          button_name: 'Orders',
          page_position: 'Top Nav',
        });
      }}
      position={position}
    >
      {ordersContent}
    </DropdownPopover>
  );
}

type AccountDropdownProps = {
  isCancelled: boolean;
  isPaused: boolean;
  testid?: string;
  onOnboardingComplete?: () => void;
  credit: string;
  accountStatus: AccountStatus;
  position?: 'left' | 'right';
};
function AccountDropdown(props: AccountDropdownProps) {
  const {
    isCancelled,
    isPaused,
    testid,
    onOnboardingComplete,
    credit,
    accountStatus,
    position,
  } = props;

  const location = useLocation();
  const { order } = useNextOrderV2();
  const { customerData } = useIdentify();
  const redesignedMhrVariant = useExperiment(171);

  const isRedesignedMhr =
    redesignedMhrVariant !== undefined && redesignedMhrVariant > 0;

  return (
    <DropdownPopover
      className={
        isCancelled || isPaused
          ? styles['account-dropdown-width-right']
          : styles['account-dropdown-width']
      }
      buttonContent={
        <div className={styles['center']}>
          <Heading variant="H6" className={styles['profile-name']}>
            {customerData?.first_name ? customerData.first_name : 'Profile'}
          </Heading>
          {isRedesignedMhr ? <Person2 /> : <Person />}
        </div>
      }
      buttonClassNames={styles['link']}
      buttonHref="profile"
      testid={testid}
      buttonTestId="profile"
      onClick={() => {
        onOnboardingComplete && onOnboardingComplete();

        buttonClickedEvent({
          target_url_path: `${location.pathname}?profile`,
          order_id: order?.id ?? null,
          button_name: 'Profile',
          page_position: 'Top Nav',
        });
      }}
      position={position}
    >
      <AccountDropdownContent credit={credit} accountStatus={accountStatus} />
    </DropdownPopover>
  );
}
